import React from "react";
import { useTranslation } from "react-i18next";
import { Pencil, Plus, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable, type Column } from "@/components/ui/data-table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageState } from "@/components/common/PageState";
import {
  AdminPartnerProviderConfiguration,
  UpsertAdminPartnerProviderRequest,
} from "@/types/admin-partner-provider";
import {
  PartnerProviderDialog,
  PartnerProviderDirtyFields,
} from "./components/PartnerProviderDialog";
import { PartnerOrdersImportPanel } from "./components/PartnerOrdersImportPanel";
import {
  CategoryGroupsAddButton,
  CategoryGroupsPanel,
} from "./components/CategoryGroupsPanel";
import {
  usePartnerProviders,
  useSavePartnerProvider,
} from "./hooks/partner-providers.hooks";
import { useAutoAssignPartnerCategoryTags } from "./hooks/partner-category-groups.hooks";
import { usePollOrdersFeed } from "./hooks/admin-hotel-partner-sync.hooks";
import { usePlatformPermissions } from "@/hooks/usePlatformPermissions";
import { PartnerProviderFormValues } from "./utils/partnerProviderSchema";

const SUPPORTED_PARTNER_PROVIDERS = ["VIATOR"] as const;

type PartnersTab = "providers" | "categories";

const getAddButtonTitle = (
  noPermission: boolean,
  noProviders: boolean,
  t: ReturnType<typeof useTranslation>["t"],
): string | undefined => {
  if (noPermission) {
    return t("common.insufficientPermissions", "Insufficient permissions");
  }
  if (noProviders) {
    return t(
      "partners.admin.noAvailableProviders",
      "No more providers available",
    );
  }
  return undefined;
};

// PUT is partial: only send a field when the user actually changed it. An
// untouched apiKey/webhookSecret/metadata field must never reach the
// request — sending an empty string clears the stored value, and sending
// `{}` for metadata wipes it (bug found in sim, 2026-09-27: an untouched
// dialog turned stored `metadata: null` into `{description:'', categories:''}`).
const buildSavePayload = (
  values: PartnerProviderFormValues,
  dirtyFields: PartnerProviderDirtyFields,
): UpsertAdminPartnerProviderRequest => {
  const payload: UpsertAdminPartnerProviderRequest = {
    baseUrl: values.baseUrl.trim(),
    syncIntervalMinutes: values.syncIntervalMinutes,
    platformFeePercent: values.platformFeePercent,
  };

  const apiKey = values.apiKey?.trim();
  if (dirtyFields.apiKey && apiKey) {
    payload.apiKey = apiKey;
  }

  const webhookSecret = values.webhookSecret?.trim();
  if (dirtyFields.webhookSecret && webhookSecret) {
    payload.webhookSecret = webhookSecret;
  }

  if (dirtyFields.description || dirtyFields.categories) {
    const description = values.description?.trim();
    const categories = values.categories?.trim();
    payload.metadata = {
      ...(description ? { description } : {}),
      ...(categories ? { categories } : {}),
    };
  }

  if (dirtyFields.defaultMaxActivitiesEnabled) {
    payload.defaultMaxActivitiesEnabled = values.defaultMaxActivitiesEnabled;
  }
  if (dirtyFields.defaultMaxActivities) {
    payload.defaultMaxActivities = values.defaultMaxActivities;
  }

  return payload;
};

export const PartnersPage: React.FC = () => {
  const { t } = useTranslation();
  const { canAdminister } = usePlatformPermissions();
  const canWrite = canAdminister("partners");
  const { data: partners = [], isLoading, isError } = usePartnerProviders();
  const savePartnerProvider = useSavePartnerProvider();
  const { pollFeed, isPolling, result: pollFeedResult } = usePollOrdersFeed();

  const [isCreateOpen, setCreateOpen] = React.useState(false);
  const [editingPartner, setEditingPartner] =
    React.useState<AdminPartnerProviderConfiguration | null>(null);
  // DV-159: Categories tab — the guest-facing groups of Viator tags.
  const [activeTab, setActiveTab] = React.useState<PartnersTab>("providers");
  const [isCreateGroupOpen, setCreateGroupOpen] = React.useState(false);
  const autoAssignTags = useAutoAssignPartnerCategoryTags();

  const handleAutoAssign = async () => {
    try {
      const result = await autoAssignTags.mutateAsync();
      toast.success(
        t("partners.admin.categoryGroups.toasts.autoAssigned", {
          count: result.assigned,
        }),
      );
    } catch {
      toast.error(t("partners.admin.categoryGroups.toasts.autoAssignError"));
    }
  };

  const configuredProviders = React.useMemo(
    () => new Set(partners.map((partner) => partner.provider)),
    [partners],
  );

  const availableProviders = React.useMemo(
    () =>
      SUPPORTED_PARTNER_PROVIDERS.filter(
        (provider) => !configuredProviders.has(provider),
      ),
    [configuredProviders],
  );

  const handleSave = async (
    values: PartnerProviderFormValues,
    dirtyFields: PartnerProviderDirtyFields,
  ) => {
    await savePartnerProvider.mutateAsync({
      provider: values.provider,
      enabled: values.enabled,
      data: buildSavePayload(values, dirtyFields),
    });
    toast.success(t("partners.admin.toasts.saveSuccess"));
    setCreateOpen(false);
    setEditingPartner(null);
  };

  const columns: Column<AdminPartnerProviderConfiguration>[] = [
    {
      key: "provider",
      header: t("partners.admin.table.provider"),
      render: (value: string) => (
        <span className="text-foreground font-medium">
          {t(`partners.providers.${value}`, { defaultValue: value })}
        </span>
      ),
    },
    {
      key: "enabled",
      header: t("partners.admin.table.enabled"),
      render: (value: boolean) => (
        <Badge variant={value ? "success" : "secondary"}>
          {value
            ? t("partners.admin.status.enabled")
            : t("partners.admin.status.disabled")}
        </Badge>
      ),
    },
    {
      key: "syncIntervalMinutes",
      header: t("partners.admin.table.syncIntervalMinutes"),
    },
    {
      key: "platformFeePercent",
      header: t("partners.admin.table.platformFeePercent"),
      render: (value: number) => `${value}%`,
    },
    {
      key: "metadata.description",
      header: t("partners.admin.table.description"),
      className: "min-w-[320px] whitespace-normal",
      render: (value: string | undefined) =>
        value ? (
          <p className="max-w-md leading-5 break-words whitespace-normal">
            {value}
          </p>
        ) : (
          "—"
        ),
    },
    {
      key: "metadata.categories",
      header: t("partners.admin.table.categories"),
      className: "min-w-[220px] whitespace-normal",
      render: (value: string | undefined) => {
        const categories = value
          ?.split(",")
          .map((category) => category.trim())
          .filter(Boolean);

        if (!categories?.length) {
          return "—";
        }

        return (
          <ul className="flex list-disc flex-col gap-1 pl-5">
            {categories.map((category) => (
              <li key={category} className="leading-5 break-words">
                {category}
              </li>
            ))}
          </ul>
        );
      },
    },
    {
      key: "hasApiKey",
      header: t("partners.admin.table.hasApiKey"),
      render: (value: boolean) => (
        <Badge variant={value ? "success" : "secondary"}>
          {value
            ? t("partners.admin.boolean.yes")
            : t("partners.admin.boolean.no")}
        </Badge>
      ),
    },
    {
      key: "hasWebhookSecret",
      header: t("partners.admin.table.hasWebhookSecret"),
      render: (value: boolean) => (
        <Badge variant={value ? "success" : "secondary"}>
          {value
            ? t("partners.admin.boolean.yes")
            : t("partners.admin.boolean.no")}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: t("partners.admin.table.actions"),
      className: "w-[88px] text-right",
      render: (_value: unknown, row) => (
        <div
          className="flex items-center justify-end"
          onClick={(event) => event.stopPropagation()}
        >
          <Button
            variant="ghost"
            size="icon"
            type="button"
            disabled={!canWrite}
            onClick={() => setEditingPartner(row)}
            aria-label={t("partners.admin.table.edit")}
            title={t("partners.admin.table.edit")}
          >
            <Pencil className="size-4" />
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <PageState text={t("partners.admin.loading")} />;
  }

  if (isError) {
    return <PageState text={t("partners.admin.loadError")} tone="error" />;
  }

  return (
    <div className="mx-auto flex flex-col gap-6 p-12">
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-foreground text-2xl font-semibold">
            {t("partners.admin.title")}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t("partners.admin.description")}
          </p>
        </div>

        {activeTab === "providers" ? (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              disabled={!canWrite || isPolling}
              onClick={() => pollFeed()}
            >
              {t("partners.admin.pollFeedButton")}
            </Button>
            <Button
              variant="icon-text"
              disabled={!canWrite || availableProviders.length === 0}
              onClick={() => setCreateOpen(true)}
              title={getAddButtonTitle(
                !canWrite,
                availableProviders.length === 0,
                t,
              )}
            >
              <Plus className="size-4" />
              {t("partners.admin.add")}
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              disabled={!canWrite || autoAssignTags.isPending}
              onClick={handleAutoAssign}
              title={t("partners.admin.categoryGroups.autoAssignHelp")}
            >
              <Wand2 className="size-4" />
              {t("partners.admin.categoryGroups.autoAssign")}
            </Button>
            <CategoryGroupsAddButton
              canWrite={canWrite}
              onClick={() => setCreateGroupOpen(true)}
            />
          </div>
        )}
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as PartnersTab)}
      >
        <TabsList>
          <TabsTrigger value="providers">
            {t("partners.admin.pageTabs.providers")}
          </TabsTrigger>
          <TabsTrigger value="categories">
            {t("partners.admin.pageTabs.categories")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="providers" className="flex flex-col gap-6">
          {pollFeedResult && (
            <div className="flex flex-col gap-2 rounded-xl border p-4">
              <h2 className="text-foreground text-sm font-semibold">
                {t("partners.admin.hotelSync.pollFeedResultTitle")} —{" "}
                {t(
                  `partners.admin.hotelSync.pollFeedStatusValues.${pollFeedResult.status}`,
                  { defaultValue: pollFeedResult.status },
                )}
              </h2>
              <div className="flex flex-wrap gap-4 text-sm">
                <span>
                  {t("partners.admin.hotelSync.pollFeedColumns.bookings")}:{" "}
                  {pollFeedResult.bookings}
                </span>
                <span>
                  {t("partners.admin.hotelSync.pollFeedColumns.hotels")}:{" "}
                  {pollFeedResult.hotels}
                </span>
                <span>
                  {t("partners.admin.hotelSync.pollFeedColumns.applied")}:{" "}
                  {pollFeedResult.applied}
                </span>
                <span>
                  {t("partners.admin.hotelSync.pollFeedColumns.failed")}:{" "}
                  {pollFeedResult.failed}
                </span>
                <span>
                  {t("partners.admin.hotelSync.pollFeedColumns.unrouted")}:{" "}
                  {pollFeedResult.unrouted}
                </span>
              </div>
              {pollFeedResult.errors.length > 0 && (
                <div className="flex flex-col gap-1">
                  <span className="text-destructive text-xs font-medium">
                    {t("partners.admin.hotelSync.pollFeedErrorsTitle")}
                  </span>
                  <ul className="text-destructive list-disc pl-5 text-xs">
                    {pollFeedResult.errors.map((error, index) => (
                      <li key={`${error}-${index}`}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <DataTable
            data={partners}
            columns={columns}
            emptyMessage={t("partners.admin.table.empty")}
            getRowKey={(row) => row.id}
            withPagination={false}
            withSorting={false}
          />

          <PartnerOrdersImportPanel canWrite={canWrite} />
        </TabsContent>

        <TabsContent value="categories">
          <CategoryGroupsPanel
            canWrite={canWrite}
            createOpen={isCreateGroupOpen}
            onCreateOpenChange={setCreateGroupOpen}
          />
        </TabsContent>
      </Tabs>

      <PartnerProviderDialog
        open={isCreateOpen}
        onOpenChange={setCreateOpen}
        availableProviders={[...availableProviders]}
        isPending={savePartnerProvider.isPending}
        onSubmit={async (values, dirtyFields) => {
          try {
            await handleSave(values, dirtyFields);
          } catch {
            toast.error(t("partners.admin.toasts.saveError"));
          }
        }}
      />

      <PartnerProviderDialog
        open={!!editingPartner}
        onOpenChange={(open) => {
          if (!open) {
            setEditingPartner(null);
          }
        }}
        partner={editingPartner}
        availableProviders={[]}
        isPending={savePartnerProvider.isPending}
        onSubmit={async (values, dirtyFields) => {
          try {
            await handleSave(values, dirtyFields);
          } catch {
            toast.error(t("partners.admin.toasts.saveError"));
          }
        }}
      />
    </div>
  );
};
